import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsApi from '../../../../libs/api/analytics';
import useFilters from '../useFilters';
import Helpers from '../../../../helpers';
import UIHelper from '../../../../helpers/UIHelper';
import moment from 'moment';
import { Table } from 'antd';
import Text from 'antd/es/typography/Text';
import { fileNameEdit } from 'helpers/FileUtils';
import _ from 'lodash';
import { usePdf } from '../../../../hooks/usePdf';

const api = new AnalyticsApi('charts/footfall_store_vs_village/');

const format = (v) => `${v}%`;

const defaultState = {
  tableData: [],
  lineChartData2: [],
  lineChartData: [],
  total: {},
  barChart: [],
  barChangeLine: [],
};

const useInvoicesTickets = (isPrint, allReportsData, isAllReports) => {
  const { t } = useTranslation();
  const { handleDownloadPdf, loadingPdf, createCustomEvent } = usePdf();
  const { filters, title, searchParams } = useFilters(
    false,
    false,
    isAllReports,
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultState);
  const [errors, setErrors] = useState([]);

  const handleDownloadExcel = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
      };
      const response = await api.getExcel({ params });
      Helpers.downloadFile(response, fileNameEdit());
    } catch (e) {
      const error = e?.response?.data?.errors || [t('Something went wrong!')];
      UIHelper.openNotificationWithIcon('error', error.join('\n'));
    } finally {
      setLoading(false);
    }
  };

  const currentYear = moment(
    searchParams.get('by_date_from_ytd'),
    'DD-MM-YYYY',
  ).year();

  const summary = useCallback(() => {
    const { total = {} } = data;

    delete total.Periodo;

    const tableKeys = Object.keys(total);
    const filteredColumns = columns.filter((column) =>
      tableKeys.includes(column.dataIndex),
    );

    return (
      <Table.Summary fixed>
        <Table.Summary.Row fixed>
          <Table.Summary.Cell index={0}>
            <Text strong>{t('Totale')}</Text>
          </Table.Summary.Cell>
          {filteredColumns.map(({ dataIndex, render = (v) => v }) => (
            <Table.Summary.Cell kry={dataIndex}>
              <Text strong>{render(total[dataIndex])}</Text>
            </Table.Summary.Cell>
          ))}
        </Table.Summary.Row>
      </Table.Summary>
    );
  }, [data]);

  const [columns, setColumns] = useState([
    {
      title: t('Periodo'),
      dataIndex: 'Periodo',
    },
    {
      title: t('Ingressi Pdv YTD'),
      dataIndex: 'Footfall Store Ytd',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Ingressi Pdv LY'),
      dataIndex: 'Footfall Store LY',
      render: (v) => Helpers.numberWithCommas(v),
    },
    {
      title: t('Ingressi PdV vs LY +/-%'),
      dataIndex: 'Ingressi Store vs LY +/-%',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Incidenza Ingressi Pdv YTD'),
      dataIndex: 'Incidenza Footfall Store YTD',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Incidenza Ingressi Pdv LY'),
      dataIndex: 'Incidenza Footfall Store LY',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Incidenza Footfall Village YTD'),
      dataIndex: 'Incidenza Footfall Village YTD',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Incidenza Footfall Village LY'),
      dataIndex: 'Incidenza Footfall Village LY',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
    {
      title: t('Footfall Village vs LY(+/-%)'),
      dataIndex: 'Footfall Village vs LY(+/-%)',
      render: (v) =>
        Helpers.numberWithCommas(Helpers.changeDotToComa(format(v))),
    },
  ]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {
          ...Object.fromEntries([...searchParams]),
        };
        if (!params.by_period || !params.by_shopping_center) return;

        let data, total;
        if (!isAllReports) {
          const {
            data: { data: backData, total: backTotal },
          } = await api.getFootfalls({ params });
          data = backData;
          total = backTotal;
        } else {
          const { data: backData, total: backTotal } = allReportsData;
          data = backData;
          total = backTotal;
        }

        if (!data) return;

        const maxValueFroLy = _.maxBy(data, 'Incidenza Footfall Store YTD')[
          'Incidenza Footfall Store YTD'
        ];
        const maxValueFroYtd = _.maxBy(data, 'Incidenza Footfall Village YTD')[
          'Incidenza Footfall Village YTD'
        ];

        const maxValue =
          maxValueFroLy > maxValueFroYtd ? maxValueFroLy : maxValueFroYtd;

        const barChart = data.map((item) => ({
          date: item['Periodo'],
          'Incidenza Ingressi Pdv YTD': item['Incidenza Footfall Store YTD'],
          'Incidenza Ingressi Pdv YTDColor': '#61cdbb',
          'Incidenza Footfall Village YTD':
            item['Incidenza Footfall Village YTD'],
          'Incidenza Footfall Village YTDColor': '#f47560',
        }));

        const barChangeLine2 = {
          id: 'Village Footfall YTD VS LY',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Footfall Village vs LY(+/-%)'],
            tooltipKeys: [],
          })),
        };

        const barChangeLine = {
          id: 'Ingressi Pdv YTD VS LY',
          data: data.map((item) => ({
            x: item['Periodo'],
            y: item['Ingressi Store vs LY +/-%'],
            tooltipKeys: [
              {
                key: 'Incidenza Ingressi Pdv YTD',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(
                    format(item['Incidenza Footfall Store YTD']),
                  ),
                ),
                color: '#61cdbb',
                percent: '',
              },
              {
                key: 'Incidenza Footfall Village YTD',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(
                    format(item['Incidenza Footfall Village YTD']),
                  ),
                ),
                color: '#f47560',
                percent: '',
              },
              {
                key: 'Ingressi Pdv YTD VS LY',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(
                    format(item['Ingressi Store vs LY +/-%']),
                  ),
                ),
                color: '#f1e15b',
                percent: '',
              },
              {
                key: 'Village Footfall YTD VS LY',
                value: Helpers.numberWithCommas(
                  Helpers.changeDotToComa(
                    format(item['Footfall Village vs LY(+/-%)']),
                  ),
                ),
                color: '#7cf15b',
                percent: '',
              },
            ],
          })),
        };

        setData({
          tableData: data,
          barChart,
          maxValue,
          barChangeLine: [barChangeLine, barChangeLine2],
          total,
        });
        setErrors([]);
      } catch (e) {
        console.log(e);
        const error = e?.response?.data?.date || [];
        if (error.length) {
          UIHelper.openNotificationWithIcon('error', error.join(', '));
        }
        setErrors(UIHelper.showAnalyticsError(e));
        setData(defaultState);
      } finally {
        setLoading(false);
        if (isPrint) {
          createCustomEvent();
        }
      }
    })();
  }, [searchParams, isPrint, allReportsData, isAllReports]);

  return {
    columns,
    data,
    filters,
    format,
    summary,
    loading,
    handleDownloadExcel,
    title,
    searchParams,
    handleDownloadPdf,
    loadingPdf,
    errors,
  };
};

export default useInvoicesTickets;
